import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { authAPI, refreshSession } from "../../services/api";

export interface User {
	id: string;
	username: string;
	email: string;
	role: string;
	isActive: boolean;
	createdAt: string;
}

export interface AuthState {
	user: User | null;
	token: string | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	// The startup session check only. App covers the screen while it runs;
	// sharing isLoading with login unmounted the form on submit, and a failed
	// login brought it back empty.
	isInitializing: boolean;
	error: string | null;
	redirectPath: string | null;
}

// Initialize state from localStorage
const getInitialAuthState = (): AuthState => {
	const token = localStorage.getItem("token");
	return {
		user: null,
		token,
		isAuthenticated: false, // Don't trust token until validated with user data
		isLoading: false,
		isInitializing: false,
		error: null,
		redirectPath: null,
	};
};

const initialState: AuthState = getInitialAuthState();

// Async thunks
export const login = createAsyncThunk(
	"auth/login",
	async (
		{ username, password }: { username: string; password: string },
		{ rejectWithValue }
	) => {
		try {
			const response = await authAPI.login(username, password);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { data?: { message?: string } };
				};
				return rejectWithValue(
					axiosError.response?.data?.message || "Login failed"
				);
			}
			return rejectWithValue("Login failed");
		}
	}
);

export const logout = createAsyncThunk("auth/logout", async () => {
	try {
		await authAPI.logout();
	} catch {
		// Continue silently even if server request fails
	}
	return undefined;
});

export const initializeAuth = createAsyncThunk(
	"auth/initialize",
	async (_, { rejectWithValue }) => {
		try {
			const token = localStorage.getItem("token");

			if (!token) {
				throw new Error("No stored credentials");
			}

			// Decode token to check if it's expired
			const base64Url = token.split(".")[1];
			const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split("")
					.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
					.join("")
			);
			const decoded = JSON.parse(jsonPayload);

			// Check if token is expired
			const currentTime = Math.floor(Date.now() / 1000);
			const isExpired = decoded.exp < currentTime;

			// If token is expired, try to refresh it
			if (isExpired) {
				// Shared with the axios interceptor: two refreshes at once would
				// replay a rotated refresh token and end the session
				const { token: freshToken, user } = await refreshSession();
				return { user: user as User, token: freshToken };
			}

			// Token is still valid, use decoded user data from token payload
			return {
				user: {
					id: decoded.userId,
					username: decoded.username,
					email: decoded.email,
					role: decoded.role,
					isActive: true, // Assume active since token is valid
					createdAt: "", // Not available in token
				},
				token,
			};
		} catch {
			// Clear invalid credentials
			localStorage.removeItem("token");

			return rejectWithValue("Invalid stored credentials");
		}
	}
);

const authSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		setCredentials: (
			state,
			action: PayloadAction<{
				user: User;
				token: string;
			}>
		) => {
			const { user, token } = action.payload;
			state.user = user;
			state.token = token;
			state.isAuthenticated = true;
			state.error = null;
			state.isLoading = false;

			localStorage.setItem("token", token);
		},
		clearCredentials: (state) => {
			state.user = null;
			state.token = null;
			state.isAuthenticated = false;
			state.error = null;
			state.redirectPath = null;

			localStorage.removeItem("token");
			localStorage.removeItem("redirectPath");
		},
		setRedirectPath: (state, action: PayloadAction<string>) => {
			state.redirectPath = action.payload;
		},
		clearRedirectPath: (state) => {
			state.redirectPath = null;
		},
	},
	extraReducers: (builder) => {
		builder
			// Login
			.addCase(login.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(login.fulfilled, (state, action) => {
				state.isLoading = false;
				state.user = action.payload.data.user;
				state.token = action.payload.data.token;
				state.isAuthenticated = true;
				state.error = null;
				localStorage.setItem("token", action.payload.data.token);
			})
			.addCase(login.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload as string;
			})

			// Logout
			.addCase(logout.fulfilled, (state) => {
				state.user = null;
				state.token = null;
				state.isAuthenticated = false;
				state.isLoading = false;
				state.error = null;
				state.redirectPath = null;

				localStorage.removeItem("token");
				localStorage.removeItem("redirectPath");
			})

			// Initialize Auth
			.addCase(initializeAuth.pending, (state) => {
				state.isInitializing = true;
			})
			.addCase(initializeAuth.fulfilled, (state, action) => {
				state.isInitializing = false;
				state.user = action.payload.user;
				state.token = action.payload.token;
				state.isAuthenticated = true;
				state.error = null;
				localStorage.setItem("token", action.payload.token);
			})
			.addCase(initializeAuth.rejected, (state) => {
				state.isInitializing = false;
				state.user = null;
				state.token = null;
				state.isAuthenticated = false;
				state.error = null;
			});
	},
});

export const {
	clearError,
	setCredentials,
	clearCredentials,
	setRedirectPath,
	clearRedirectPath,
} = authSlice.actions;
export default authSlice.reducer;
